import React from "react";

import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import MarketplacePrice from "@hl/shared-features/lib/features/marketplace/components/MarketplacePrice";
import AcceptBidModalHighlight, {
  AcceptBidModalTitle,
} from "@hl/shared-features/lib/features/marketplace/modals/AcceptBidModal";
import BidModalHighlight from "@hl/shared-features/lib/features/marketplace/modals/BidModal";
import CancelBidModalHighlight from "@hl/shared-features/lib/features/marketplace/modals/CancelBidModal";
import ManageOffersModal from "@hl/shared-features/lib/features/marketplace/modals/ManageOffersModal";
import { TokenOffersQuery } from "@hl/shared-features/lib/features/marketplace/queries/marketplace.graphql.generated";
import { MarketplaceCollection } from "@hl/shared-features/lib/features/marketplace/utils";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import {
  Button,
  Center,
  Divider,
  Flex,
  Loader,
  Stack,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";

import useMarketplaceState from "~features/token-details/hook/useMarketplaceState";
import useMintState from "~hooks/useMintState";

import { SeriesToken } from "../../apollo/graphql.generated";

type TokenOffer = TokenOffersQuery["getTokenOffers"]["edges"][number];

const TokenDetailsSidebarOffers = ({ token }: { token: SeriesToken }) => {
  const { collection, chainId } = useMintState();
  const collectionData: MarketplaceCollection = {
    name: collection?.name ?? "",
    address: collection?.address ?? "",
    chainId: chainId,
    id: collection?.id,
    marketplaceId: collection?.marketplaceId,
    onchainId: collection?.onchainId ?? null,
    standard: collection?.standard,
    imageUrl: collection?.collectionImage ?? "",
    collectionType: collection?.collectionType,
    editionId: collection?.entityId ?? collection?.editionId,
  };
  const { walletAddress } = useAuth();
  const theme = useMantineTheme();
  const { pushModal } = useModalStack();
  const { refetchToken, bids, isLoadingBids, refetchBids } =
    useMarketplaceState();
  const isTokenOwner =
    walletAddress?.toLowerCase() === token.ownerAddress?.toLowerCase() ||
    !!token.userBalance;

  const openAcceptBidModal = (bid: TokenOffer) => {
    pushModal(
      <AcceptBidModalHighlight
        bid={bid}
        collectionData={collectionData}
        tokenId={token?.tokenId ?? undefined}
        onCompleted={() => {
          refetchBids();
          refetchToken?.();
        }}
      />,
      {
        size: "lg",
        title: (
          <AcceptBidModalTitle
            address={bid.makerAddress}
            isCollection={bid.criteriaKind !== "token"}
          />
        ),
      }
    );
  };

  const openCancelBidModal = (bid: TokenOffer) => {
    pushModal(
      <CancelBidModalHighlight
        collectionData={collection!}
        imageUrl={token ? token.imageUrl ?? "" : bid.collection?.imageUrl ?? ""}
        id={bid.reservoirId}
        floorPrice={bid.floorPrice ?? undefined}
        tokenId={token?.id}
        onCancel={() => setTimeout(() => refetchBids(), 5000)}
      />,
      {
        size: "lg",
        title: <Text fw={WEIGHT_BOLD}>Cancel your offer</Text>,
      }
    );
  };
  const bidsToShow = bids?.slice(0, 5) || [];
  const getConditionText = (bid: TokenOffer) => {
    if (bid.criteriaKind === "attribute") {
      return ` “${bid.attributeKey}: ${bid.attributeValue}” trait offer`;
    }
    if (bid.criteriaKind === "collection") {
      return " collection offer";
    }
    return " token offer";
  };

  return (
    <>
      {bidsToShow.map((bid, index) => {
        const isBidMaker =
          bid.makerAddress.toLowerCase() === walletAddress?.toLowerCase();
        return (
          <Stack key={index} spacing={0}>
            <Flex
              gap="sm"
              py="sm"
              align="center"
              justify="space-between"
              sx={{
                cursor: isTokenOwner || isBidMaker ? "pointer" : "default",
              }}
              onClick={() =>
                isTokenOwner && !isBidMaker
                  ? openAcceptBidModal(bid)
                  : isBidMaker
                  ? openCancelBidModal(bid)
                  : null
              }
            >
              <Text
                display="flex"
                size="sm"
                sx={{
                  whiteSpace: "pre",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <Text
                  size="sm"
                  mr="sm"
                  color={theme.colors.placeholderText[0]}
                  fw={WEIGHT_BOLD}
                >
                  {index + 1}
                </Text>
                <MarketplacePrice
                  size="sm"
                  price={bid.price}
                  fw={WEIGHT_BOLD}
                />
                <Text
                  size="sm"
                  sx={{ textOverflow: "ellipsis", overflow: "hidden" }}
                >
                  {getConditionText(bid)}
                </Text>
              </Text>
              {(isTokenOwner || isBidMaker) && (
                <Button size="xs" color={SECONDARY_COLOR}>
                  {isTokenOwner && !isBidMaker ? "Accept" : "Cancel"}
                </Button>
              )}
            </Flex>
            {index < bidsToShow.length - 1 && <Divider />}
          </Stack>
        );
      })}
      {isLoadingBids && (
        <Center mt={24}>
          <Loader />
        </Center>
      )}
      {isTokenOwner &&
        !isLoadingBids &&
        !!bids &&
        bids.length > 5 &&
        collection && (
          <Flex justify="center" w="100%">
            <UnstyledButton
              py="sm"
              mb="xs"
              disabled={!isTokenOwner}
              onClick={() =>
                pushModal(
                  <ManageOffersModal
                    collectionData={collectionData}
                    tokenId={token?.tokenId ?? ""}
                    onCompleted={() => setTimeout(() => refetchToken?.(), 3000)}
                  />,
                  {
                    size: "100%",
                    title: (
                      <Text fw={WEIGHT_BOLD}>
                        Manage offers for {token?.name} · {collection?.name}
                      </Text>
                    ),
                  }
                )
              }
            >
              <Text size="sm" fw={WEIGHT_BOLD}>
                View all offers
              </Text>
            </UnstyledButton>
          </Flex>
        )}
      {!isLoadingBids && !!bids && bids.length === 0 && collection && (
        <Text py="sm" mb="sm" size="sm" color={theme.colors.secondaryText[0]}>
          There are no active offers for this token.{" "}
          {!isTokenOwner && (
            <>
              Be the first to
              <UnstyledButton
                onClick={() =>
                  pushModal(
                    <BidModalHighlight
                      tokenId={token.tokenId!}
                      src={token.imageUrl || ""}
                      collectionData={collection}
                      onCompleted={refetchBids}
                    />,
                    {
                      size: "lg",
                      title: (
                        <Text size="md" fw={WEIGHT_BOLD}>
                          Make an offer
                        </Text>
                      ),
                    }
                  )
                }
              >
                <Text
                  color={theme.colors.primaryText[0]}
                  size="sm"
                  fw={WEIGHT_BOLD}
                  sx={{ whiteSpace: "pre" }}
                >
                  {" "}
                  make an offer.
                </Text>
              </UnstyledButton>
            </>
          )}
        </Text>
      )}
    </>
  );
};

export default TokenDetailsSidebarOffers;
